import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../Modules/Shared/Services/api.service';
import {
  IndexQueryParams,
  IndexResponse,
  ShowResponse,
  WipePayload,
  FreshStartPayload,
} from '../../Interfaces/api/Intune/devices.interface';
import { HttpParams, HttpResourceRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { endpoints } from '../../../environments/endpoints';
import { LazyLoadListResponse, NoContentResponse } from '../../Modules/Shared/Interfaces/response.interface';
import { IntuneDevice } from '../../Models/Intune/intune-device.model';

@Injectable({
  providedIn: 'root',
})
export class DevicesService {
  private _apiService = inject(ApiService);

  index(queryParams?: IndexQueryParams, skipLoading: boolean = true): HttpResourceRequest {
    const params = this.buildQueryParams(queryParams);
    return this._apiService.get<IndexResponse>(endpoints.api.intune.devices.index, {
      params,
      skipLoading,
    });
  }

  next(
    lazyloadingId: string,
    skipLoading: boolean = true,
  ): Observable<LazyLoadListResponse<IntuneDevice>> {
    const request = this._apiService.get<LazyLoadListResponse<IntuneDevice>>(
      endpoints.api.intune.devices.next(lazyloadingId),
      {
        skipLoading,
      },
    );
    return this._apiService.run<LazyLoadListResponse<IntuneDevice>>(request);
  }

  show(deviceId: string, skipLoading: boolean = true): HttpResourceRequest {
    return this._apiService.get<ShowResponse>(endpoints.api.intune.devices.show(deviceId), {
      skipLoading,
    });
  }

  wipe(deviceId: string, payload: WipePayload, skipLoading = false): Observable<NoContentResponse> {
    const request = this._apiService.post<NoContentResponse>(
      endpoints.api.intune.devices.wipe(deviceId),
      { body: payload, skipLoading },
    );
    return this._apiService.run<NoContentResponse>(request);
  }

  freshStart(
    deviceId: string,
    payload?: FreshStartPayload,
    skipLoading = false,
  ): Observable<NoContentResponse> {
    const request = this._apiService.post<NoContentResponse>(
      endpoints.api.intune.devices.freshStart(deviceId),
      { body: payload ?? {}, skipLoading },
    );
    return this._apiService.run<NoContentResponse>(request);
  }

  retrieveBitlocker(deviceId: string, skipLoading = false): Observable<unknown> {
    const request = this._apiService.post<unknown>(
      endpoints.api.intune.devices.retrieveBitlocker(deviceId),
      { skipLoading },
    );
    return this._apiService.run<unknown>(request);
  }

  autopilotReset(deviceId: string, skipLoading = false): Observable<NoContentResponse> {
    const request = this._apiService.post<NoContentResponse>(
      endpoints.api.intune.devices.autopilotReset(deviceId),
      { skipLoading },
    );
    return this._apiService.run<NoContentResponse>(request);
  }

  rotateLocalCredentials(deviceId: string, skipLoading = false): Observable<NoContentResponse> {
    const request = this._apiService.post<NoContentResponse>(
      endpoints.api.intune.devices.rotateLocalCredentials(deviceId),
      { skipLoading },
    );
    return this._apiService.run<NoContentResponse>(request);
  }

  private buildQueryParams(queryParams?: IndexQueryParams): HttpParams {
    let params = new HttpParams();
    params = params.set('top', queryParams?.top?.toString() ?? '5');
    if (queryParams?.search) params = params.set('search', queryParams.search);
    if (queryParams?.top) params = params.set('top', queryParams.top.toString());
    if (queryParams?.orderBy) params = params.set('orderBy', queryParams.orderBy);
    if (queryParams?.order) params = params.set('order', queryParams.order);
    if (queryParams?.select?.length) {
      queryParams.select.forEach((field) => {
        params = params.append('select[]', field);
      });
    }
    return params;
  }
}
